/**
 * Global trial-expired detection + redirect for the Admin Console
 * (Phase 3d, mirror of the learner-app equivalent).
 *
 * Backend surfaces 403 with body `{ error_code: 'trial_expired' }` on every
 * 10-point TrialAccessService wiring surface after day 14 revocation. The
 * admin app uses a mix of axios (restApiService) and `window.fetch` (e.g.
 * analytics Governor Pack downloads). We intercept BOTH so no surface
 * leaves the user stranded on a raw 403.
 *
 * Install once at module boot from App.tsx. Idempotent.
 */
import { AxiosInstance, AxiosError } from "axios";

const TRIAL_EXPIRED_ROUTE = "/trial-expired";
const TRIAL_EXPIRED_CODE = "trial_expired";

let installed = false;
let redirecting = false;

function navigateToTrialExpired() {
  if (redirecting) return;
  if (window.location.pathname === TRIAL_EXPIRED_ROUTE) return;
  redirecting = true;
  window.location.replace(TRIAL_EXPIRED_ROUTE);
}

function bodyIsTrialExpired(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  const maybe = body as { error_code?: unknown };
  return maybe.error_code === TRIAL_EXPIRED_CODE;
}

function installFetchInterceptor() {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    if (response.status === 401 || response.status === 403) {
      const contentType = response.headers.get("content-type") ?? "";
      if (contentType.includes("application/json")) {
        try {
          const clone = response.clone();
          const body = await clone.json();
          if (bodyIsTrialExpired(body)) {
            navigateToTrialExpired();
          }
        } catch {
          // body not JSON-parseable; ignore
        }
      }
    }
    return response;
  };
}

export function installAxiosTrialExpiredInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        if (bodyIsTrialExpired(error.response?.data)) {
          navigateToTrialExpired();
        }
      }
      return Promise.reject(error);
    },
  );
}

export function installTrialExpiredHandler(axiosInstances: AxiosInstance[] = []) {
  if (installed) return;
  installed = true;
  installFetchInterceptor();
  for (const instance of axiosInstances) {
    installAxiosTrialExpiredInterceptor(instance);
  }
}
